// src/components/wells/WellAiExplainer.js
// FlowSense MOW+ — AI explainer for one well: why the risk score is what it is, what to do next,
// what data is missing, plus the live level, flood model, drainage, inspection and work orders.
import React, { memo, useMemo } from 'react';
import {
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter, DrawerCloseButton,
  Box, Flex, HStack, VStack, Text, Badge, Icon, Button, Progress, CircularProgress, CircularProgressLabel,
  SimpleGrid, Divider, Tooltip, Alert, AlertIcon, useColorModeValue,
} from '@chakra-ui/react';
import {
  MdAutoAwesome, MdLightbulbOutline, MdInfoOutline, MdWaves, MdAssignment, MdSensors, MdMyLocation,
  MdBuild, MdTrendingUp, MdTrendingDown, MdTrendingFlat, MdWarning, MdHistory,
} from 'react-icons/md';
import { AI_GRADIENT, PRIORITY_SCHEME, INSPECTION_META, tierMeta, formatAgo, LevelSparkline, wellKindLabel, drainRoleMeta } from './wellIntelUi';
import GroundElevationCard from '../elevation/GroundElevationCard'; // --- UPDATE (wells v1.3)
import { MdListAlt } from 'react-icons/md';

const Section = ({ icon, title, right, children }) => {
  const muted = useColorModeValue('gray.500', 'gray.400');
  return (
    <Box>
      <Flex justify="space-between" align="center" mb={2}>
        <HStack spacing={1.5}><Icon as={icon} color="purple.400" boxSize={4} />
          <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color={muted}>{title}</Text>
        </HStack>
        {right}
      </Flex>
      {children}
    </Box>
  );
};

function WellAiExplainer({ isOpen, onClose, well, onLocate, onCreateWorkOrder, onOpenWorkOrder, onViewHistory }) {
  const card = useColorModeValue('gray.50', 'whiteAlpha.50');
  const border = useColorModeValue('gray.200', 'whiteAlpha.200');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const heroBg = useColorModeValue('linear(to-br, white, purple.50)', 'linear(to-br, gray.800, rgba(68, 51, 122, 0.4))');

  const intel = well?.intel;
  const tm = tierMeta(intel?.tier);
  const maxPts = useMemo(() => Math.max(1, ...(intel?.drivers || []).map((d) => d.points)), [intel]);
  const flood = well?.flood;
  const trend = well?.trend;
  const TrendIcon = trend?.trend === 'rising' ? MdTrendingUp : trend?.trend === 'falling' ? MdTrendingDown : MdTrendingFlat;
  const insp = well?.inspection;
  const inspMeta = INSPECTION_META[insp?.status] || INSPECTION_META.never_inspected;
  const hasCoords = well?.latitude != null && well?.longitude != null;

  return (
    <Drawer isOpen={isOpen && !!well} onClose={onClose} placement="right" size="md">
      <DrawerOverlay backdropFilter="blur(4px)" />
      <DrawerContent borderLeftRadius="2xl" overflow="hidden">
        <Box h="3px" bgGradient={AI_GRADIENT} />
        <DrawerCloseButton mt={2} />
        <DrawerHeader borderBottomWidth="1px" borderColor={border}>
          <HStack spacing={3}>
            <Box p={2} borderRadius="xl" bgGradient={AI_GRADIENT} color="white"><Icon as={MdAutoAwesome} boxSize={5} /></Box>
            <VStack align="start" spacing={0} minW={0}>
              <Text fontWeight="900" fontSize="lg" noOfLines={1}>{well?.name || `Well ${well?.id}`}</Text>
              <HStack spacing={2} fontSize="xs" color={muted}>
                <Badge fontSize="9px" colorScheme={well?.well_kind === 'drainage' ? 'cyan' : 'blue'} variant="subtle">{well?.asset_type || wellKindLabel(well)}</Badge>
                <Text fontFamily="mono">{well?.id}</Text>
                {well?.asset_criticality && <Badge fontSize="9px" colorScheme="purple" variant="subtle">{well.asset_criticality} criticality</Badge>}
                <Text>· {intel?.model_version}</Text>
              </HStack>
            </VStack>
          </HStack>
        </DrawerHeader>

        <DrawerBody py={5}>
          {well && intel && (
            <VStack align="stretch" spacing={5}>
              {/* Score + summary */}
              <Flex p={4} gap={4} borderRadius="2xl" borderWidth="1.5px" borderColor={`${tm.scheme}.200`} bgGradient={heroBg} align="center">
                <CircularProgress value={intel.score ?? 0} size="84px" thickness="10px" color={tm.color} trackColor={border}>
                  <CircularProgressLabel fontWeight="900" fontSize="lg">{intel.score ?? '—'}</CircularProgressLabel>
                </CircularProgress>
                <VStack align="start" spacing={1} flex="1">
                  <HStack spacing={2}>
                    <Badge colorScheme={tm.scheme} variant="solid" borderRadius="full" px={2}>{tm.label}</Badge>
                    <Tooltip label="How much of the evidence FlowSense needs is available for this well" hasArrow>
                      <Badge colorScheme="purple" variant="subtle" borderRadius="full">{Math.round((intel.confidence || 0) * 100)}% confidence</Badge>
                    </Tooltip>
                  </HStack>
                  <Text fontSize="sm" fontWeight="600">{intel.summary}</Text>
                </VStack>
              </Flex>

              {/* Why: drivers */}
              <Section icon={MdInfoOutline} title="Why this score">
                {intel.drivers.length === 0 ? (
                  <Text fontSize="sm" color={muted}>No risk drivers in the current data.</Text>
                ) : (
                  <VStack align="stretch" spacing={2.5}>
                    {intel.drivers.map((d) => (
                      <Box key={d.key}>
                        <Flex justify="space-between" fontSize="sm">
                          <Text fontWeight="700">{d.label}</Text>
                          <Text fontFamily="mono" color={muted}>+{d.points}</Text>
                        </Flex>
                        <Progress value={(d.points / maxPts) * 100} size="xs" colorScheme={d.points >= 15 ? 'red' : d.points >= 6 ? 'orange' : 'purple'} borderRadius="full" my={1} />
                        <Text fontSize="xs" color={muted}>{d.evidence}</Text>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Section>

              {/* What to do */}
              {intel.recommendations.length > 0 && (
                <Section icon={MdLightbulbOutline} title="Recommended actions">
                  <VStack align="stretch" spacing={2}>
                    {intel.recommendations.map((r) => (
                      <Flex key={r.code} p={2.5} gap={2} borderRadius="lg" bg={card} borderWidth="1px" borderColor={border} align="center">
                        <Badge colorScheme={PRIORITY_SCHEME[r.priority] || 'gray'} fontSize="9px">{r.priority}</Badge>
                        <Text fontSize="sm" flex="1">{r.text}</Text>
                        {onCreateWorkOrder && !['MAP_DRAINAGE'].includes(r.code) && (
                          <Button size="xs" colorScheme="purple" variant="ghost" leftIcon={<MdBuild />} onClick={() => onCreateWorkOrder(well, r)}>Work order</Button>
                        )}
                      </Flex>
                    ))}
                  </VStack>
                </Section>
              )}

              {intel.data_gaps.length > 0 && (
                <Alert status="info" borderRadius="lg" alignItems="start" fontSize="sm">
                  <AlertIcon mt={0.5} />
                  <VStack align="start" spacing={0.5}>
                    <Text fontWeight="700">Data gaps (lower confidence)</Text>
                    {intel.data_gaps.map((g) => <Text key={g}>• {g}</Text>)}
                  </VStack>
                </Alert>
              )}

              <Divider />

              {/* Live level + flood model */}
              <Section icon={MdSensors} title="Live level & flood model" right={well.latest_reading?.timestamp && <Text fontSize="xs" color={muted}>{formatAgo(well.latest_reading.timestamp)}</Text>}>
                {!well.monitoring?.sensor_count ? (
                  <Text fontSize="sm" color={muted}>No sensor linked. Link a water-level sensor to monitor this well.</Text>
                ) : (
                  <SimpleGrid columns={3} spacing={2}>
                    <Box p={2.5} bg={card} borderRadius="lg" textAlign="center">
                      <Text fontSize="10px" color={muted} fontWeight="700">LEVEL</Text>
                      <Text fontWeight="900">{well.latest_reading ? String(well.latest_reading.level ?? well.latest_reading.value).toUpperCase() : '—'}</Text>
                      <Text fontSize="10px" color={muted}>{well.monitoring.online_count}/{well.monitoring.sensor_count} online</Text>
                    </Box>
                    <Box p={2.5} bg={card} borderRadius="lg" textAlign="center">
                      <Text fontSize="10px" color={muted} fontWeight="700">FLOOD PROBABILITY</Text>
                      <Text fontWeight="900" color={flood?.probability >= 0.65 ? 'red.500' : 'purple.500'}>
                        {flood?.probability != null ? `${Math.round(flood.probability * 100)}%` : '—'}
                      </Text>
                      <Text fontSize="10px" color={muted} noOfLines={1}>
                        {flood?.est_minutes_to_overtopping != null ? `overtop ~${flood.est_minutes_to_overtopping}m` : flood?.severity || (flood?.reason ? 'no prediction' : '')}
                      </Text>
                    </Box>
                    <Box p={2.5} bg={card} borderRadius="lg" textAlign="center" color={trend?.trend === 'rising' ? 'red.400' : trend?.trend === 'falling' ? 'green.400' : muted}>
                      <Text fontSize="10px" color={muted} fontWeight="700">24 H TREND</Text>
                      <HStack justify="center" spacing={1}><Icon as={TrendIcon} /><Text fontWeight="900">{trend?.rate_pct_per_hour != null ? `${trend.rate_pct_per_hour > 0 ? '+' : ''}${trend.rate_pct_per_hour}%/h` : '—'}</Text></HStack>
                      <Box mt={1} display="flex" justifyContent="center"><LevelSparkline values={trend?.series} width={90} height={20} /></Box>
                    </Box>
                  </SimpleGrid>
                )}
                {flood?.reason && flood?.probability == null && <Text mt={2} fontSize="xs" color={muted}>{flood.reason}</Text>}
              </Section>

              {/* Drainage / flooding system */}
              <Section icon={MdWaves} title={`Drainage within ${well.drainage?.radius_m ?? ''} m`}>
                {!hasCoords ? <Text fontSize="sm" color={muted}>Add coordinates to this well to link it to the drainage network.</Text>
                  : !well.drainage?.nearby?.length ? <Text fontSize="sm" color={muted}>No drainage asset mapped nearby.</Text>
                    : (
                      <VStack align="stretch" spacing={1.5}>
                        {/* --- UPDATE (wells v1.3): catch basins / drainage wells matched to this well with their inspections */}
                        {well.drainage.nearby.map((d) => {
                          const rm = drainRoleMeta(d.role);
                          const di = d.inspection || {};
                          const im = INSPECTION_META[di.status] || INSPECTION_META.never_inspected;
                          return (
                            <Box key={String(d.id)} fontSize="sm" p={2} borderRadius="md" bg={card}>
                              <Flex justify="space-between" gap={2}>
                                <HStack minW={0}><Badge fontSize="8px" colorScheme={rm.scheme}>{rm.label}</Badge><Text fontWeight="700" noOfLines={1}>{d.name || d.id}</Text></HStack>
                                <HStack spacing={2} flexShrink={0}>
                                  <Text fontSize="xs" color={muted}>{d.distance_m} m</Text>
                                  <Badge colorScheme={d.impaired ? 'red' : 'green'} fontSize="9px">{d.impaired ? d.reason : d.status || 'ok'}</Badge>
                                </HStack>
                              </Flex>
                              <HStack mt={1} spacing={2} fontSize="10px" color={muted} flexWrap="wrap">
                                <Badge fontSize="8px" colorScheme={im.scheme} variant="subtle">{im.label}{di.overdue_days ? ` · ${di.overdue_days}d` : ''}</Badge>
                                <Text>inspected {di.last_inspected_date || '—'}</Text>
                                {di.clearance_score != null && <Text color={di.clearance_score < 50 ? 'red.400' : undefined}>clearance {di.clearance_score}%</Text>}
                                {d.open_work_orders > 0 && <Text color="purple.400">{d.open_work_orders} open WO</Text>}
                                {d.elevation_drop_m != null && (
                                  <Tooltip label="Ground elevation of this well minus the drainage asset (TessaDEM). Positive: run-off flows to it." hasArrow>
                                    <Text color={d.elevation_drop_m < 0 ? 'orange.400' : undefined}>{d.elevation_drop_m >= 0 ? `${d.elevation_drop_m} m downhill` : `${Math.abs(d.elevation_drop_m)} m uphill`}</Text>
                                  </Tooltip>
                                )}
                              </HStack>
                            </Box>
                          );
                        })}
                        {/* --- END UPDATE */}
                      </VStack>
                    )}
              </Section>

              {/* --- UPDATE (wells v1.3): ground elevation + terrain (TessaDEM) */}
              <GroundElevationCard assetId={well.id} compact />
              {/* --- END UPDATE */}

              {/* Asset register (inventory data as recorded) */}
              <Section icon={MdListAlt} title="Asset register">
                <SimpleGrid columns={2} spacing={2} fontSize="sm">
                  <Box p={2} bg={card} borderRadius="md"><Text fontSize="10px" color={muted} fontWeight="700">TYPE</Text><Text fontWeight="700">{well.asset_type || wellKindLabel(well)}</Text></Box>
                  <Box p={2} bg={card} borderRadius="md"><Text fontSize="10px" color={muted} fontWeight="700">STATUS</Text><Text fontWeight="700" textTransform="capitalize">{well.status || '—'}</Text></Box>
                  {well.location_description && <Box p={2} bg={card} borderRadius="md" gridColumn="span 2"><Text fontSize="10px" color={muted} fontWeight="700">LOCATION</Text><Text fontWeight="700">{well.location_description}</Text></Box>}
                  {hasCoords && <Box p={2} bg={card} borderRadius="md"><Text fontSize="10px" color={muted} fontWeight="700">COORDINATES</Text><Text fontWeight="700" fontFamily="mono" fontSize="xs">{Number(well.latitude).toFixed(5)}, {Number(well.longitude).toFixed(5)}</Text></Box>}
                  {well.install_date && <Box p={2} bg={card} borderRadius="md"><Text fontSize="10px" color={muted} fontWeight="700">INSTALLED</Text><Text fontWeight="700">{String(well.install_date).slice(0, 10)}</Text></Box>}
                  {well.metadata?.base_depth_m != null && <Box p={2} bg={card} borderRadius="md"><Text fontSize="10px" color={muted} fontWeight="700">DESIGN DEPTH</Text><Text fontWeight="700">{well.metadata.base_depth_m} m</Text></Box>}
                  {well.metadata?.well_depth_m != null && <Box p={2} bg={card} borderRadius="md"><Text fontSize="10px" color={muted} fontWeight="700">MEASURED DEPTH</Text><Text fontWeight="700">{well.metadata.well_depth_m} m</Text></Box>}
                  {(well.register || []).filter((f) => !['base_depth_m', 'well_depth_m'].includes(f.key)).map((f) => (
                    <Box key={f.key} p={2} bg={card} borderRadius="md"><Text fontSize="10px" color={muted} fontWeight="700" textTransform="uppercase">{f.label}</Text><Text fontWeight="700">{String(f.value)}</Text></Box>
                  ))}
                </SimpleGrid>
              </Section>

              {/* Inspection */}
              <Section icon={MdHistory} title="Inspection & cleaning" right={<Badge colorScheme={inspMeta.scheme}>{inspMeta.label}</Badge>}>
                <SimpleGrid columns={2} spacing={2} fontSize="sm">
                  <Box p={2} bg={card} borderRadius="md"><Text fontSize="10px" color={muted} fontWeight="700">LAST INSPECTED</Text>
                    <Text fontWeight="700">{insp?.last_inspected_date || '—'}</Text>
                    <Text fontSize="xs" color={muted}>{insp?.last_inspected_by || ''}</Text></Box>
                  <Box p={2} bg={card} borderRadius="md"><Text fontSize="10px" color={muted} fontWeight="700">NEXT DUE</Text>
                    <Text fontWeight="700" color={insp?.overdue_days ? 'red.500' : undefined}>{insp?.next_due_date || 'Schedule now'}</Text>
                    <Text fontSize="xs" color={muted}>every {insp?.interval_days} days{insp?.overdue_days ? ` · ${insp.overdue_days}d overdue` : ''}</Text></Box>
                  <Box p={2} bg={card} borderRadius="md"><Text fontSize="10px" color={muted} fontWeight="700">CLEARANCE</Text>
                    <Text fontWeight="700" color={insp?.clearance_score != null && insp.clearance_score < 70 ? 'orange.400' : undefined}>{insp?.clearance_score != null ? `${insp.clearance_score}%` : '—'}</Text>
                    <Text fontSize="xs" color={muted}>{insp?.clearance_trend ? `${insp.clearance_trend} over ${insp.clearance_history.length} inspections` : `${insp?.inspection_count || 0} inspections`}</Text></Box>
                  <Box p={2} bg={card} borderRadius="md"><Text fontSize="10px" color={muted} fontWeight="700">CASING · CLEANED</Text>
                    <Text fontWeight="700" textTransform="capitalize">{insp?.casing_condition || '—'}</Text>
                    <Text fontSize="xs" color={insp?.cleaning_overdue_days ? 'orange.400' : muted}>{insp?.last_cleaned_date ? `cleaned ${insp.last_cleaned_date}` : 'no cleaning record'}</Text></Box>
                </SimpleGrid>
              </Section>

              {/* Work orders + alerts */}
              <Section icon={MdAssignment} title={`Work orders (${well.work_orders?.length || 0}) · alerts (${well.alert_count || 0})`}>
                <VStack align="stretch" spacing={1.5}>
                  {(well.work_orders || []).map((w) => (
                    <Flex key={String(w.id)} p={2} borderRadius="md" bg={card} justify="space-between" align="center" fontSize="sm" cursor={onOpenWorkOrder ? 'pointer' : 'default'} onClick={() => onOpenWorkOrder?.(w)}>
                      <Text fontWeight="700" noOfLines={1}>{w.title || w.id}</Text>
                      <HStack spacing={1} flexShrink={0}>
                        {w.overdue && <Badge colorScheme="red" fontSize="9px">overdue</Badge>}
                        <Badge fontSize="9px">{w.status}</Badge>
                      </HStack>
                    </Flex>
                  ))}
                  {(well.alerts || []).slice(0, 4).map((a) => (
                    <HStack key={`a-${a.id}`} fontSize="xs" spacing={1.5}><Icon as={MdWarning} color="orange.400" /><Text noOfLines={1}>{a.alert_message}</Text><Text color={muted}>{formatAgo(a.triggered_at)}</Text></HStack>
                  ))}
                  {!well.work_orders?.length && !well.alert_count && <Text fontSize="sm" color={muted}>No open work orders or active alerts.</Text>}
                </VStack>
              </Section>
            </VStack>
          )}
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor={border} gap={2} flexWrap="wrap">
          {onLocate && hasCoords && <Button size="sm" variant="outline" leftIcon={<MdMyLocation />} onClick={() => onLocate(well)}>Locate</Button>}
          {onViewHistory && well?.monitoring?.primary_device_id && (
            <Button size="sm" variant="outline" leftIcon={<MdHistory />} onClick={() => onViewHistory(well.monitoring.primary_device_id)}>Level history</Button>
          )}
          {onCreateWorkOrder && (
            <Button size="sm" bgGradient={AI_GRADIENT} color="white" _hover={{ opacity: 0.9 }} leftIcon={<MdBuild />} onClick={() => onCreateWorkOrder(well, intel?.recommendations?.[0])}>
              Create work order
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

export default memo(WellAiExplainer);
